<template>
  <div>
    <div v-if="banner && themeConfig.showChatBox" class="chatbox" @click="goHelpCenter">
      <i><img :src="banner.image" alt=""></i>
    </div>

    <header>
      <!-- 手機版「詳細」入口：開啟左側抽屜（原為底部 dock 第一項，樣式見 refresh/shared-shell.css） -->
      <button
        v-if="isMobile()"
        ref="headerToggler"
        type="button"
        class="header-toggler"
        :aria-label="$t('Bao.home_detail')"
        :aria-expanded="String(toggleNav)"
        @click="toggleNav = true"
      >
        <img :src="$getThemeImagePath('icon-header-toggler.png')" alt="">
      </button>
      <div class="header-logo" @click="goHome">
        <img id="header-logo-m" :src="brand.info.h5_image" alt="logo for mobile devices" height="49px">
      </div>
      <div v-if="!token" class="signin-block">
        <div v-if="!isLoginOnly" class="sign-in" :class="{ active: isRegisterActive }" @click="goSignup()">
          {{ $t("Bao.register") }}
        </div>
        <div class="sign-up" :class="{ active: isLoginActive }" @click="goLogin()">
          {{ $t("Bao.login") }}
        </div>
      </div>

      <div v-if="token" id="signup" class="signup-block">
        <!-- 手機版（≤767px）錢包選單為底部面板：遮罩、Esc、關閉鈕、下拉關閉與焦點限制（樣式見 refresh/shared-shell.css） -->
        <div class="lang-select crypto-wallet-frame header-wallet-frame userWallet-mb" :class="{'active': showUserInfo}" @click="openUserInfo($event)">
          <button
            ref="walletTrigger"
            type="button"
            class="btn-select icon-select-expand"
            aria-haspopup="dialog"
            aria-controls="header-wallet-sheet"
            :aria-expanded="String(isWalletSheetOpen)"
          >
            <li>
              <img
                :key="`header-wallet-${selectedWallet.currency}-${selectedWallet.wallet_type}`"
                :src="getWalletImagePath(selectedWallet)"
                :alt="selectedWallet.currency"
              >
              <div class="crypto-box">
                <p class="amounts">
                  {{ getWalletBalance(selectedWallet) }}
                </p>
              </div>
            </li>
          </button>
          <div
            v-show="showUserInfo && filteredWalletInfo.length > 0"
            id="header-wallet-sheet"
            ref="walletSheet"
            class="b active"
            :role="isWalletSheetOpen ? 'dialog' : null"
            :aria-modal="isWalletSheetOpen ? 'true' : null"
            :aria-labelledby="isWalletSheetOpen ? 'header-wallet-title' : null"
            tabindex="-1"
            :style="walletSheetDragY ? { transform: `translateY(${walletSheetDragY}px)` } : null"
            @click.stop
            @keydown="onWalletSheetKeydown"
          >
            <div
              class="wallet-sheet-head"
              @pointerdown="onWalletSheetDragStart"
              @pointermove="onWalletSheetDragMove"
              @pointerup="onWalletSheetDragEnd"
              @pointercancel="onWalletSheetDragEnd"
            >
              <button type="button" class="wallet-sheet-grabber" :aria-label="$t('Bao.close')" @click="onWalletSheetHeadClose" />
              <h2 id="header-wallet-title">
                {{ $t('Bao.wallet_type') }}
              </h2>
              <button type="button" class="wallet-sheet-close" :aria-label="$t('Bao.close')" @click="onWalletSheetHeadClose">
                ×
              </button>
            </div>
            <ul ref="walletList" class="crypto-list" role="radiogroup" :aria-label="$t('Bao.select_display_currency')">
              <li
                v-for="wallet in filteredWalletInfo"
                :key="`wallet-item-${ wallet.currency }-${wallet.wallet_type}`"
                :data-value="wallet.currency"
                :class="{ active: isWalletSelected(wallet) }"
                role="radio"
                :aria-checked="String(isWalletSelected(wallet))"
                :tabindex="isWalletSelected(wallet) ? 0 : -1"
                @click.stop="selectWallet(wallet)"
              >
                <img
                  :key="`wallet-img-${ wallet.currency }-${wallet.wallet_type}`"
                  :src="getWalletImagePath(wallet)"
                  :alt="wallet.currency"
                >
                <div class="crypto-box">
                  <b class="currency">{{ wallet.name }}</b>
                  <p class="amounts">
                    {{ getWalletBalance(wallet) }}
                  </p>
                </div>
              </li>
            </ul>
            <div class="logged-in-frame">
              <div class="hr-line" />
              <button v-if="$store.getters.canMemberDeposit" class="btn btn-nav-deposit" @click.stop="goToDeposit">
                {{ $t('Bao.deposit') }}
              </button>
              <button class="btn btn-nav-withdrawal" @click.stop="goToWithdrawal">
                {{ $t('Bao.withdrawal') }}
              </button>
            </div>
          </div>
          <i class="fa-solid fa-angle-right" aria-hidden="true" />
          <div class="wallet-sheet-backdrop" :hidden="!isWalletSheetOpen" @click.stop="closeWalletSheet" />
        </div>

        <!-- 餘額重新整理：請求中 disabled + is-refreshing（手機版通知鈴已移除，訊息入口在詳細選單） -->
        <button
          type="button"
          class="header-wallet-refresh"
          :class="{ 'is-refreshing': isRefreshingGold }"
          :aria-busy="isRefreshingGold ? 'true' : null"
          :disabled="isRefreshingGold"
          @click.stop="refreshGold()"
        >
          <img :src="$getThemeImagePath('icon-refresh.png')" alt="refresh gold">
        </button>
      </div>
    </header>

    <!-- 手機版詳細選單為左側抽屜，由 header 的 header-toggler 開啟（樣式見 refresh/shared-shell.css） -->
    <div class="left-menu" :class="{ active: toggleNav }">
      <nav ref="navMenu" :class="{ active: toggleNav }">
        <div v-show="toggleNav" class="nav-close" @click="toggleNav = false">
          <img :src="$getThemeImagePath('icon_close.png')" alt="close button">
        </div>
        <!-- 左上方-註冊登錄－未登入 -->
        <template v-if="!token">
          <!-- nav-header-guest：手機詳細選單收起訪客頂部區（切版以 #logged-in-box.D-none 判斷，nihon 未登入不渲染該區塊） -->
          <div class="nav-header nav-header-guest login-bg">
            <div class="nav-no-login">
              <!-- nav-logo-plain：非 gold 主題不加圓形底盤，顯示上傳 logo 原圖（樣式見 refresh/shared-shell.css） -->
              <div class="nav-login-logo" :class="{ 'nav-logo-plain': !themeConfig.showNavLogoPlate }">
                <a @click.prevent="goHome"><img :src="brandInfo.image" alt="home"></a>
              </div>
              <div class="nav-no-login-button">
                <button v-if="!isLoginOnly" class="button-style-3 btn-primary btn-nav-sign-in" tag="button" @click="goSignup()">
                  <span>{{ $t("Bao.register") }}</span>
                </button>
                <button class="button-style-3 btn-primary btn-nav-sign-up-pc" tag="button" @click="goLogin()">
                  <span>{{ $t('Bao.login') }}</span>
                </button>
              </div>
            </div>
          </div>
        </template>
        <!-- 左上方個人資訊-已登入 -->
        <template v-if="token && !isMobile()">
          <div class="nav-header login-bg">
            <div class="nav-login">
              <div class="nav-login-logo" :class="{ 'nav-logo-plain': !themeConfig.showNavLogoPlate }" @click="goHome">
                <img :src="brandInfo.image" alt="home">
              </div>
              <div id="logged-in-box" class="user-info">
                <div class="user-info-name" @click="$router.push('/user')">
                  <div class="user-info-text">
                    <span class="h4">{{ name }}</span>
                    <span>{{ $t('Bao.username') }}</span>
                  </div>
                </div>
                <div class="user-info-level">
                  <span class="h5">{{ $t('Bao.level') }}:</span>
                  <div>
                    <span class="vipgrade">{{ user.vipInfo.currentVipName }}</span>
                  </div>
                </div>
                <div class="lang-select crypto-wallet-frame userWallet" :class="{ active: showUserInfo }" @click="openUserInfo($event)">
                  <button class="btn-select icon-select-expand">
                    <li>
                      <img
                        :key="`header-wallet-${selectedWallet.currency}-${selectedWallet.wallet_type}`"
                        :src="getWalletImagePath(selectedWallet)"
                        :alt="selectedWallet.currency"
                      >
                      <div class="crypto-box">
                        <p class="amounts">
                          {{ getWalletBalance(selectedWallet) }}
                        </p>
                      </div>
                    </li>
                  </button>
                  <div v-show="showUserInfo && filteredWalletInfo.length > 0" class="b active">
                    <ul class="crypto-list">
                      <li
                        v-for="wallet in filteredWalletInfo"
                        :key="`wallet-item-${ wallet.currency }-${ wallet.wallet_type}`"
                        :data-value="wallet.currency"
                        :class="{ active: isWalletSelected(wallet) }"
                        @click.stop="selectWallet(wallet)"
                      >
                        <img
                          :key="`wallet-img-${ wallet.currency }-${wallet.wallet_type}`"
                          :src="getWalletImagePath(wallet)"
                          :alt="wallet.currency"
                        >
                        <div class="crypto-box">
                          <b class="currency">{{ wallet.name }}</b>
                          <p class="amounts">
                            {{ getWalletBalance(wallet) }}
                          </p>
                        </div>
                      </li>
                    </ul>
                  </div>
                  <i class="fa-solid fa-angle-right" />
                  <!-- 錢包餘額重新整理：請求中加 is-refreshing（樣式見 refresh/shared-shell.css） -->
                  <button
                    type="button"
                    class="wallet-refresh"
                    :class="{ 'is-refreshing': isRefreshingGold }"
                    :aria-busy="isRefreshingGold ? 'true' : null"
                    :disabled="isRefreshingGold"
                    @click.stop="refreshGold()"
                  >
                    <img :src="$getThemeImagePath('icon-refresh.png')" alt="refresh gold">
                    <span class="wallet-refresh-dots" aria-hidden="true" />
                  </button>
                </div>
                <div class="nav-login-button">
                  <button
                    v-show="brand.permissionInfo.enable_deposit === 1 && $store.getters.canMemberDeposit"
                    class="button-style-3 btn-nav-deposit"
                    @click="goToDeposit"
                  >
                    <span>{{ $t('Bao.deposit') }}</span>
                  </button>
                  <button
                    v-show="brand.permissionInfo.enable_withdrawal === 1"
                    class="button-style-3 btn-nav-withdrawal"
                    @click="goToWithdrawal"
                  >
                    <span>{{ $t('Bao.withdrawal') }}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>
        <!-- 左側選單-未登入 -->
        <div v-if="!token" class="nav-link-list">
          <div v-if="themeConfig.showEvent" ref="navOther" class="link-item" :class="{ active: path === '/event' }" :aria-current="path === '/event' ? 'page' : null">
            <div
              class="link-item-name brush-item"
              @click="$router.push('/event')"
            >
              <div class="link-item-image" data-nav-icon="event" />
              <div class="link-item-text">
                <div class="nav">
                  <h1 v-if="path === '/event'">
                    {{ $t("Bao.event") }}
                  </h1>
                  <span v-else>{{ $t("Bao.event") }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="user.lotteryId" class="link-item" :class="{ 'active': user.wheelOpen }" :aria-current="user.wheelOpen ? 'page' : null">
            <div class="link-item-name brush-item" @click="openWheel">
              <div class="link-item-image">
                <img :src="$getThemeImagePath('fortunewheel.png')" alt="FortuneWheel for daily spin">
              </div>
              <div class="link-item-text">
                <div class="nav">
                  <span>{{ $t("Bao.roulette") }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- 每日活動 -->
          <div
            v-if="brandImageCheckKey('top_left_corner_icon') && isOpenDailyAttend"
            class="link-item"
          >
            <div
              v-for="(item, key) in brandImage.top_left_corner_icon"
              :key="key"
              class="link-item-name brush-item"
              @click="getDailyItem(item)"
            >
              <template v-if="item.link == 'DailyBox'">
                <div class="link-item-image">
                  <img :src="$getThemeImagePath('daily-attend.png')" alt="Daily attend">
                  <span>{{ item.name }}</span>
                </div>
              </template>
              <template v-else>
                <div class="link-item-image">
                  <img :src="$getThemeImagePath('daily-attend.png')" alt="Daily attend">
                </div>
                <div class="link-item-text">
                  <div class="nav">
                    <span>{{ item.name }}</span>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div v-if="brand.permissionInfo.enable_agent_frontend_display !== 0" class="link-item">
            <div
              class="link-item-name brush-item"
              @click="goProxy"
            >
              <div class="link-item-image" data-nav-icon="affiliate" />
              <div class="link-item-text">
                <div class="nav">
                  <span>{{ $t('Bao.affiliates') }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="link-item" :class="{ active: path === '/vip' }" :aria-current="path === '/vip' ? 'page' : null">
            <div
              class="link-item-name brush-item"
              @click="$router.push('/vip')"
            >
              <div class="link-item-image" data-nav-icon="vip" />
              <div class="link-item-text">
                <div class="nav">
                  <h1 v-if="path === '/vip'">
                    {{ $t("Bao.vip_club") }}
                  </h1>
                  <span v-else>{{ $t("Bao.vip_club") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 左側選單-已登入 -->
        <div v-if="token" class="nav-link-list">
          <div ref="navPersonal" class="link-item" :class="{ active: toogleMyAccout }">
            <div class="link-item-name" @click="toogleMyAccout = !toogleMyAccout">
              <div class="link-item-image" data-nav-icon="account" />
              <div class="link-item-text">
                <div class="nav">
                  <span>{{ $t("Bao.personal") }}</span>
                  <i class="fa-solid fa-angle-right" />
                </div>
              </div>
            </div>

            <ul class="link-item-dropdown">
              <li
                class="brush-item"
                :class="{ active: path === '/user' }"
                :aria-current="path === '/user' ? 'page' : null"
                @click="handleLinkClick('/user')"
              >
                <div class="link-item-text">
                  <span>{{ $t("Bao.profile") }}</span>
                </div>
              </li>
              <li
                class="brush-item"
                :class="{ active: path === '/user/bet-history' }"
                :aria-current="path === '/user/bet-history' ? 'page' : null"
                @click="handleLinkClick('/user/bet-history')"
              >
                <div class="link-item-text">
                  <span>{{ $t("Bao.my_game_bet") }}</span>
                </div>
              </li>
              <li
                class="brush-item"
                :class="{ active: path === '/user/transaction-history' }"
                :aria-current="path === '/user/transaction-history' ? 'page' : null"
                @click="handleLinkClick('/user/transaction-history?type=depositRecord')"
              >
                <div class="link-item-text">
                  <span>{{ $t("Bao.transaction-index") }}</span>
                </div>
              </li>
              <li
                v-if="themeConfig.showBankCreate"
                class="brush-item"
                :class="{ active:
                  path === '/user/bank' || path === '/user/bank/create' }"
                :aria-current="path === '/user/bank' || path === '/user/bank/create' ? 'page' : null"
                @click="handleLinkClick('/user/bank')"
              >
                <div class="link-item-text">
                  <span>{{ $t("Bao.add_bank_card") }}</span>
                </div>
              </li>
              <li
                v-if="themeConfig.showUserLetter"
                class="brush-item"
                :class="{ active: path === '/user/letter' }"
                :aria-current="path === '/user/letter' ? 'page' : null"
                @click="handleLinkClick('/user/letter')"
              >
                <div class="link-item-text">
                  <span>{{ $t("Bao.announcement_letter") }}</span>
                </div>
              </li>
              <li
                v-if="themeConfig.showNews"
                class="brush-item"
                :class="{ active: path === '/user/news' }"
                :aria-current="path === '/user/news' ? 'page' : null"
                @click="handleLinkClick('/user/news')"
              >
                <div class="link-item-text">
                  <span>{{ $t("Bao.news") }}</span>
                </div>
              </li>
            </ul>
          </div>
          <div v-if="themeConfig.showEvent" ref="navOther" class="link-item" :class="{ active: path === '/event' }" :aria-current="path === '/event' ? 'page' : null">
            <div
              class="link-item-name brush-item"
              @click="$router.push('/event')"
            >
              <div class="link-item-image" data-nav-icon="event" />
              <div class="link-item-text">
                <div class="nav">
                  <h1 v-if="path === '/event'">
                    {{ $t("Bao.event") }}
                  </h1>
                  <span v-else>{{ $t("Bao.event") }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="user.lotteryId" class="link-item" :class="{ 'active': user.wheelOpen }" :aria-current="user.wheelOpen ? 'page' : null">
            <div
              class="link-item-name brush-item"
              @click="openWheel"
            >
              <div class="link-item-image">
                <img :src="$getThemeImagePath('fortunewheel.png')" alt="FortuneWheel for daily spin">
              </div>
              <div class="link-item-text">
                <div class="nav">
                  <span>{{ $t("Bao.roulette") }}</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="brand.permissionInfo.enable_agent_frontend_display !== 0" class="link-item">
            <div
              class="link-item-name brush-item"
              @click="goProxy"
            >
              <div class="link-item-image" data-nav-icon="affiliate" />
              <div class="link-item-text">
                <div class="nav">
                  <span>{{ $t('Bao.affiliates') }}</span>
                </div>
              </div>
            </div>
          </div>

          <div v-if="token" class="link-item" :class="{ active: path === '/points/all-bonus' || path === '/points/coupon' }" :aria-current="path === '/points/all-bonus' || path === '/points/coupon' ? 'page' : null">
            <div
              class="link-item-name brush-item"
              @click="jumptoShop"
            >
              <div class="link-item-image" data-nav-icon="shop" />
              <div class="link-item-text">
                <div class="nav">
                  <span>{{ $t("Bao.shop") }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="link-item" :class="{ active: path === '/vip' }" :aria-current="path === '/vip' ? 'page' : null">
            <div
              class="link-item-name brush-item"
              @click="$router.push('/vip')"
            >
              <div class="link-item-image" data-nav-icon="vip" />
              <div class="link-item-text">
                <div class="nav">
                  <h1 v-if="path === '/vip'">
                    {{ $t("Bao.vip_club") }}
                  </h1>
                  <span v-else>{{ $t("Bao.vip_club") }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- 左側選單-首頁 -->
        <template>
          <div ref="navGameCategory" class="nav-link-list">
            <div class="link-item" :class="{ active: toggleHOT }">
              <div class="link-item-name" @click="toggleHOT = !toggleHOT">
                <div class="link-item-image" data-nav-icon="recommended" />
                <div class="link-item-text">
                  <div class="nav">
                    <span>{{ $t("Bao.recommend_game") }}</span>
                    <i class="fa-solid fa-angle-right" />
                  </div>
                </div>
              </div>
              <ul class="link-item-dropdown nav-uploaded-icons">
                <li
                  v-show="hasActiveFavorite"
                  class="brush-item inner"
                  :class="{ 'active': activeFilter === 'favorites' }"
                  :aria-current="activeFilter === 'favorites' ? 'page' : null"
                  @click="changeGameCategory(null)"
                >
                  <div
                    class="link-item-image"
                    :class="{ 'original-image-icon': hasFeature('showFavoriteImage') }"
                    :data-category-icon="hasFeature('showFavoriteImage') ? null : 'favorites'"
                  >
                    <img :src="$getThemeImagePath('favorite.png')" alt="change game type">
                  </div>
                  <div class="link-item-text">
                    <h2><span>{{ $t("Bao.my_favorite") }}</span></h2>
                  </div>
                </li>
                <li
                  v-for="(item, key) in gameCategory"
                  :key="'game_' + key"
                  class="brush-item inner"
                  :class="{ 'active': item === selectedTab }"
                  :aria-current="item === selectedTab ? 'page' : null"
                  @click="changeGameCategory(item)"
                >
                  <div class="link-item-image" :class="{ 'original-image-icon': hasFeature('showCategoryImage') }">
                    <img :src="item.image" alt="change game category">
                  </div>
                  <div class="link-item-text">
                    <h2><span>{{ item.name }}</span></h2>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </template>

        <!--共用功能區塊-->
        <template>
          <div class="nav-link-list">
            <!-- <div v-if="token" class="link-item" @click="handleLinkClick('/user/friend')">
              <div class="link-item-name brush-item" :class="{ 'active': path === '/user/friend' }">
                <div class="link-item-image">
                  <img :src="$getThemeImagePath('friend_promo.png')" alt="friend promo">
                </div>
                <div class="link-item-text">
                  <span> {{ $t("Bao.promos") }}</span>
                </div>
              </div>
            </div> -->
            <div v-if="token" class="link-item" :class="{ active: path === '/user/edit' || path === '/user/KYC' || path === '/user/verify' }" :aria-current="path === '/user/edit' || path === '/user/KYC' || path === '/user/verify' ? 'page' : null">
              <div
                class="link-item-name brush-item"
                @click="$router.push('/user/edit')"
              >
                <div class="link-item-image" data-nav-icon="setting" />
                <div class="link-item-text">
                  <div class="nav">
                    <span>{{ $t("Bao.verify_account") }}</span>
                  </div>
                </div>
              </div>
            </div>
            <!--客服-->
            <!-- 客服快捷列：直接放在 nav-link-list 底下（對應切版結構），quick-bar 只負責初始化客服 -->
            <div v-for="(item, key) in quickBars" :key="'quick-bar-' + key" class="link-item brush-item">
              <div class="link-item-name" @click="goQuickBar(item)">
                <div class="link-item-image">
                  <img :src="item.image" alt="">
                </div>
                <div class="link-item-text">
                  <div class="nav">
                    <span>{{ item.label }}</span>
                  </div>
                </div>
              </div>
            </div>
            <quick-bar />
            <div v-if="themeConfig.showSupportCenter" class="link-item" :class="{ active: path === '/page/support' }" :aria-current="path === '/page/support' ? 'page' : null">
              <div
                class="link-item-name brush-item"
                @click="$router.push('/page/support')"
              >
                <div class="link-item-image" data-nav-icon="help" />
                <div class="link-item-text">
                  <div class="nav">
                    <span>{{ $t('Bao.support_center') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="themeConfig.showBlog" class="link-item">
              <div
                class="link-item-name brush-item"
                @click="openBlog"
              >
                <div class="link-item-image" data-nav-icon="news" />
                <div class="link-item-text">
                  <div class="nav">
                    <span>{{ $t('Bao.blog_text') }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>

        <div class="nav-link-list">
          <div
            class="link-item"
            :class="{ active: toogleSelectLanguage }"
          >
            <div
              class="language-item"
              @click="toogleSelectLanguage = !toogleSelectLanguage"
            >
              <div class="language-item-current pl-2" :class="{ active: toogleSelectLanguage }">
                <span>{{ $t(`Bao.languages_${selectedLanguage}`) }}</span>
                <i class="fa-solid fa-angle-right" />
              </div>
              <ul>
                <li
                  v-for="(item, key) in brandInfo.language"
                  :key="key"
                  @click="changeLanguage(item)"
                >
                  <span>{{ $t(`Bao.languages_${item}`) }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div v-if="themeConfig.showSocialMedia" class="nav-link-list">
          <h4>{{ $t('Bao.social_media') }}</h4>
          <div class="social-web-frame">
            <div v-for="(media, key) in followMedia" :key="key" class="social-icons-frame">
              <a class="social-icons" :href="media.link" :target="'_' + media.open_type"><img :src="media.image" alt=""></a>
            </div>
          </div>
        </div>
        <!--登出按鈕-->
        <button v-show="token" class="button-style-4 nav-logout-button" @click="logout">
          <span>{{ $t('Bao.logout') }}</span>
        </button>
      </nav>
    </div>
    <main :class="isModalOpen">
      <router-view />
      <!-- 頁腳 -->
      <footer-vue />
      <!-- 手機版-底部 -->
      <div v-if="isMobile()" class="nav-bottom" :class="{ hidden: !showNav }">
        <!-- 手機版-底部-選單 -->
        <div class="nav-bottom-menu">
          <!-- 選取底板跟隨 li.active 移動（utils/dockMotion.js）
               順序：ショップ／入金／アカウント／サポート（依 themeConfig.showSupportCenter，同側選單），搜尋固定在右側；
               沒有サポート時三項沿用四格寬度置中 -->
          <ul ref="dockTrack" @keydown="onDockKeydown">
            <li
              data-identifier="ft-mall"
              role="button"
              tabindex="0"
              :class="{ active: isActiveTab('shop') }"
              @click="setActiveTab('shop')"
            >
              <i><img :src="$getThemeImagePath('nav-outline/shop.svg')" alt="shop"></i>
              <span>{{ $t('Bao.shop') }}</span>
            </li>
            <li
              v-if="$store.getters.canMemberDeposit"
              data-identifier="ft-deposit"
              role="button"
              tabindex="0"
              :class="{ active: isActiveTab('deposit') }"
              @click="setActiveTab('deposit')"
            >
              <i><img :src="$getThemeImagePath('nav-bottom-deposit.png')" alt="deposit"></i>
              <span>{{ $t('Bao.deposit') }}</span>
            </li>
            <li
              data-identifier="ft-acc"
              role="button"
              tabindex="0"
              :class="{ active: isActiveTab('my') }"
              @click="setActiveTab('my')"
            >
              <i><img :src="$getThemeImagePath('nav-bottom-my.png')" alt="user"></i>
              <span>{{ $t('Bao.my') }}</span>
            </li>
            <li
              v-if="themeConfig.showSupportCenter"
              data-identifier="ft-support"
              role="button"
              tabindex="0"
              :class="{ active: isActiveTab('support') }"
              @click="goDockSupport"
            >
              <i><img :src="$getThemeImagePath('nav-outline/support.svg')" alt="support"></i>
              <span>{{ quickBars[0].label }}</span>
            </li>
            <!-- 手機版遊戲搜尋 -->
            <li id="nav-ft-search" role="button" tabindex="0" @click="openMobileSearch">
              <i><img :src="$getThemeImagePath('icon-game-search.png')" alt="game search"></i>
              <span>{{ $t('Bao.find_game') }}</span>
            </li>
          </ul>
        </div>
      </div>
    </main>
    <div v-if="isMobile()" class="mobile-menu-backdrop" aria-hidden="true" @click="toggleNav = false" />
    <!-- 線下站只有帳號登入，改用代理帳號登入彈窗 -->
    <agent-login-modal v-if="!token && isLoginOnly" />
    <register-modal v-if="!token && !isLoginOnly" />
    <login-modal v-if="!token && !isLoginOnly" />
    <set-username-modal v-if=" !token" />
    <forget-modal v-if=" !token" />
    <success-register />
    <daily-notice-modal
      v-if="showDailyNotice"
      :is-active="true"
      :daily-notice-data="dailyNoticeData"
      @hideModal="showDailyNotice = false"
    />

    <Toast />
    <wheel-vue />
  </div>
</template>

<script>
import { getEvent, getBetRankList } from '@/api/event'
import { info } from '@/api/user'
import { getGameList } from '@/api/game'
import quickBar from '@/components/quickBar'
import loginModal from '@/components/modal/loginModal'
import agentLoginModal from '@/components/modal/agentLoginModal'
import Toast from '@/components/modal/toast'
import forgetModal from '@/components/modal/forgetModal'
import registerModal from '@/components/modal/registerModal'
import setUsernameModal from '@/components/modal/setUsernameModal'
import Cookies from 'js-cookie'
import DailyNoticeModal from '@/components/modal/DailyNoticeModal.vue'
import { page } from '@/api/common'
import { isCrossDay } from '@/utils/tool'
import { showNotice, getWalletImagePath, getFilteredWalletInfo, handleHelpCenterLink, detectMob, getAffiliateUrl } from '@/utils/tool'
import { logout as localLogout, isPersonalDataComplete } from '@/utils/user'
import { initDockMotion } from '@/utils/dockMotion'
import SuccessRegister from '@/components/modal/successRegister.vue'
import wheelVue from '../../components/modal/wheel.vue'
import footerVue from '@/components/footer'
import { mapGetters } from 'vuex'
import themeMixin from '@/mixins/themeMixin'
import walletMixin from '@/mixins/walletMixin'

export default {
  name: 'Main',
  components: {
    quickBar,
    loginModal,
    agentLoginModal,
    registerModal,
    setUsernameModal,
    forgetModal,
    Toast,
    SuccessRegister,
    wheelVue,
    footerVue,
    DailyNoticeModal
  },
  mixins: [themeMixin, walletMixin],
  data() {
    return {
      toggleNav: false,
      isWalletSheetViewport: false,
      walletSheetDrag: null,
      walletSheetDragY: 0,
      suppressWalletSheetClickUntil: 0,
      toogleSelectLanguage: false,
      toggleHOT: true,
      toogleMyAccout: false,
      selectedLanguage: '',
      events: [],
      sponsor_footer: null,
      footerIntro: null,
      activeTab: '',
      selectedTab: null,
      showNav: true,
      lastScrollTop: 0,
      isOpenDailyAttend: false,
      showUserInfo: false,
      showDailyNotice: false,
      dailyNoticeData: {},
      selectedWalletId: null,
      hasActiveFavorite: false
    }
  },
  computed: {
    ...mapGetters(['token', 'user', 'memberInfo', 'brand', 'brandInfo', 'brandImage', 'brandImageCheckKey', 'game', 'point', 'name', 'app', 'walletInfo', 'isOfflineAgentMember', 'isLoginOnly']),
    path() {
      return this.$route.path
    },
    banner() {
      return this.brand.banners[0]
    },
    isLoginActive() {
      return this.user.isLoginActive
    },
    isRegisterActive() {
      return this.user.isRegisterActive
    },
    loginOnlyRegisterRequest() {
      return this.isLoginOnly && this.isRegisterActive
    },
    isForgetActive() {
      return this.user.isForgetActive
    },
    isModalOpen() {
      return this.isLoginActive ||
             this.isRegisterActive ||
             this.isForgetActive ||
             this.brand.showSearch ||
             this.brand.showProvider ||
             this.toggleNav
    },
    gameCategory() {
      const category = Array.isArray(this.$store.getters.gameCategory)
        ? this.$store.getters.gameCategory.slice()
        : []

      return [...category].sort((a, b) => a.sorting - b.sorting)
    },
    currentGameCategory() {
      return this.user.currentGameCategory
    },
    currentGameProvider() {
      return this.user.currentGameProvider
    },
    showHeaderHome() {
      return !this.token &&
      this.isLoginActive || this.isRegisterActive || this.isForgetActive
    },
    showHeaderNotification() {
      if (this.isHome && !this.brand.showSearch && !this.brand.showProvider && this.token) {
        return !this.currentGameProvider
      }
      return false
    },
    followMedia() {
      return this.brandImage.follow_media
    },
    gameUrlFromStore() {
      return this.app.gameUrl
    },
    allGoldSum() {
      if (!this.walletInfo || !Array.isArray(this.walletInfo) || this.walletInfo.length === 0) {
        return 0
      }

      const mainWallet = this.walletInfo.find(wallet =>
        wallet && wallet.wallet_type === 'main' && wallet.currency === this.defaultWalletCurrency
      )
      const bonusWallet = this.walletInfo.find(wallet =>
        wallet && wallet.wallet_type === 'bonus' && wallet.currency === this.defaultWalletCurrency
      )

      const mainBalance = mainWallet ? (Number(mainWallet.balance) || 0) : 0
      const bonusBalance = bonusWallet ? (Number(bonusWallet.balance) || 0) : 0
      return mainBalance + bonusBalance
    },
    // 客服快捷列（web 類型），原本由 quickBar 元件渲染
    quickBars() {
      return (this.$store.state.brand.quickBars || []).filter(item => item.type === 'web')
    },
    isHome() {
      return this.$route.name === 'home'
    },
    activeFilter() {
      return this.user.activeFilter
    },
    filteredWalletInfo() {
      return getFilteredWalletInfo(this.walletInfo, this.brandInfo, this.isOfflineAgentMember)
    },
    // 預設幣別
    defaultWalletCurrency() {
      return this.isTheme('theme_neotri') ? 'USD' : 'JPY'
    },
    selectedWallet() {
      // 優先用 store 的值
      const storeWallet = this.app.selectedWallet
      if (storeWallet && Object.keys(storeWallet).length > 0) {
        // 從 filteredWalletInfo 找對應的（確保資料是最新的）
        const found = this.filteredWalletInfo.find(
          w => w.wallet_type === storeWallet.wallet_type &&
           w.currency === storeWallet.currency
        )
        if (found) return found
      }

      if (this.selectedWalletId) {
        const found = this.filteredWalletInfo.find(
          w => w.wallet_type === this.selectedWalletId.wallet_type &&
           w.currency === this.selectedWalletId.currency
        )
        if (found) return found
      }

      const defaultMain = this.filteredWalletInfo.find(w => w.wallet_type === 'main' && w.currency === this.defaultWalletCurrency)
      return defaultMain || (this.filteredWalletInfo.length > 0 ? this.filteredWalletInfo[0] : {
        wallet_type: 'main',
        currency: this.defaultWalletCurrency,
        balance: 0
      })
    },
    walletSwitchRequest() {
      return this.$store.state.app.walletSwitchRequest
    },
    // 手機版錢包選單以底部面板（modal）呈現；較寬的 header 仍是原本的下拉選單
    isWalletSheetOpen() {
      return this.isWalletSheetViewport && this.showUserInfo && this.filteredWalletInfo.length > 0
    }
  },
  watch: {
    // 最愛遊戲被後台關閉後不算數，全部都被關閉時隱藏「我的最愛」（同首頁 isActiveGame 判斷）
    'user.favoriteGame': {
      async handler(favoriteGame) {
        if (!favoriteGame || favoriteGame.length === 0) {
          this.hasActiveFavorite = false
          return
        }
        const gameIds = [...new Set(favoriteGame.map(fav => fav.game_id))]
        try {
          const response = await getGameList({ game_id_list_string: JSON.stringify(gameIds) }, { noLoading: true })
          this.hasActiveFavorite = (response.data.data || []).some(game => game.status === 1 && game.is_show === 1)
        } catch (err) {
          console.log(err)
        }
      },
      immediate: true
    },
    // 線下站不開放註冊：所有開啟註冊的入口（Banner、邀請碼、落地頁等）一律改開登入；品牌資料較晚取得時也會補切換
    loginOnlyRegisterRequest: {
      handler(shouldSwitch) {
        if (!shouldSwitch) return
        this.$store.commit('SET_REGISTER_OPEN', false)
        this.$store.commit('SET_LOGIN_OPEN', true)
      },
      immediate: true
    },
    walletSwitchRequest: {
      handler(payload) {
        if (!payload || !payload.wallet) return
        this.selectWallet(payload.wallet)
      }
    },
    gameUrlFromStore(newVal, oldVal) {
      if (newVal !== oldVal) {
        const headerElement = document.querySelector('header')
        if (newVal) {
          headerElement.style.display = 'none'
          this.disableScrolling()
        } else {
          this.enableScrolling()
          headerElement.style.display = window.innerWidth < 992 ? 'block' : 'none'
        }
      }
    },
    'activeTab'(to, from) {
      let targetPath = ''
      if (to === 'offers') targetPath = '/event'
      else if (to === 'deposit') targetPath = '/deposit'
      else if (to === 'my') targetPath = '/user'
      else if (to === 'shop') targetPath = '/points/all-bonus'
      else if (to === 'support') targetPath = '/page/support'

      if (to === 'my' && this.path.startsWith('/user')) {
        return
      }

      if (to === 'shop' && this.path.startsWith('/points')) {
        return
      }

      if (to === 'deposit' && (this.brand.permissionInfo.enable_deposit !== 1 || !this.$store.getters.canMemberDeposit)) {
        showNotice(this.$t('Bao.deposit_maintain'))
        this.activeTab = ''
        if (!this.isHome) {
          this.$router.push('/')
        }
      } else if (this.$route.path !== targetPath) {
        if (to === 'deposit' && this.isUserDataComplete() && !this.isTheme('theme_purple')) {
          showNotice(this.$t('Bao.deposit_notes'), () => {
            this.$router.push({
              path: '/deposit'
            })
          }, 'removeClose')

          return
        }
        this.$router.push(targetPath)
          .catch(() => {
            this.activeTab = ''
          })
      }
    },
    '$route'(to, from) {
      this.checkIsHomeLeftMenu()
      this.setToggleNavAndCommit()

      // mobile bottom nav
      if (!this.isHome) {
        this.selectedTab = null
        this.$store.commit('SET_GAME_URL', null)
        this.app.tempGameUrl = ''
      }

      this.activeTab = this.getRouteTab()
    },
    toggleNav(newVal) {
      this.toggleBodyStyle(newVal)
      if (this.isMobile()) {
        document.body.classList.toggle('mobile-menu-open', newVal)
        // 關閉抽屜後焦點回到 header 的詳細按鈕
        if (!newVal && this.$refs.headerToggler) this.$refs.headerToggler.focus({ preventScroll: true })
      }
    },
    isWalletSheetOpen(open) {
      this.syncWalletSheetModal(open)
    },
    'currentGameCategory'(to) {
      if (to && this.activeFilter !== 'favorites') {
        this.selectedTab = to
        this.toggleHOT = true

        const category = this.gameCategory.find(c => c.seo_path === to.seo_path)
        if (category && category.seo_path !== '') {
          // 如果目前網址沒有正確參數，就補上
          if (this.$route.params.seo_path !== category.seo_path) {
            // 以 push 新增歷史紀錄，上一頁才能回到前一個分類／首頁
            this.$router.push({ params: { seo_path: category.seo_path }})
          }
        }

        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToNavElement('navGameCategory')
          }, 400)
        })
      } else {
        this.selectedTab = null
      }
    },
    isModalOpen(newValue) {
      if (newValue) {
        this.disableScrolling()
      } else {
        this.enableScrolling()

        if (this.user.isFromMenu) {
          this.toggleNav = true
          this.$store.commit('SET_FROM_MENU', false)
        }
      }
    },
    'token'(newVal, oldVal) {
      if (newVal && !oldVal) {
        this.checkDailyNotice()
        info().then(response => {
          const reqData = response.data
          this.$store.dispatch('SetUserData', reqData.data)

          const walletInfo = reqData.data.walletInfo
          this.$store.commit('SET_WALLET_INFO', walletInfo)

          const selectedWallet = walletInfo.find(wallet => wallet.wallet_type === this.app.selectedWallet.wallet_type)
          this.$store.commit('SET_SELECTED_WALLET', selectedWallet)

          setTimeout(() => {
            this.initSelectedWallet()
          }, 30)
        }).catch((e) => {
          console.log('Error refreshing user info after login:', e)
        })
      }
    },
    'activeFilter'(to) {
      if (to === 'favorites') {
        this.$nextTick(() => {
          setTimeout(() => {
            this.scrollToNavElement('navGameCategory')
          }, 400)
        })
      }
    },
    'walletInfo'(newVal, oldVal) {
      // When walletInfo is updated, initialize wallet selection
      if (newVal && newVal.length > 0 && (!oldVal || oldVal.length === 0)) {
        console.log('walletInfo updated, initializing wallet selection')
        this.$nextTick(() => {
          this.initSelectedWallet()
        })
      }
    },
    'app.selectedWallet': {
      immediate: true,
      handler(newVal) {
        if (!newVal || Object.keys(newVal).length === 0) {
          this.initSelectedWallet()
        }
      }
    }
  },
  created() {
    this.init()

    let currentLangKey = this.brandInfo.language[0]
    if (this.$route.query.language) {
      currentLangKey = this.$route.query.language
      this.$router.replace({ query: {}})
    } else if (Cookies.get('language')) {
      currentLangKey = Cookies.get('language')
    }

    this.$store.commit('SET_LANGUAGE', currentLangKey)
    this.setLanguage(currentLangKey)

    if (this.token) {
      this.checkDailyNotice()
    }
    // News
    getEvent({
      page: 1,
      page_size: 50
    }).then((response) => {
      this.events = response.data.data.list
      this.$store.commit('ADD_EVENTS', this.events)
      this.dispatchV4Events()
    })

    // mobile bottom nav
    this.activeTab = this.getRouteTab()

    getBetRankList().then(response => {
      const eventArr = []
      response.data.event.forEach(item => {
        if (item.status === 1 && item.is_sys_disabled === 0) {
          eventArr.push(item)
        }
      })
      this.$store.commit('SET_CHAMPIONSHIP_EVENT', eventArr)
    })
  },
  mounted() {
    this.toggleBodyStyle(this.toggleNav)

    window.addEventListener('scroll', this.handleScroll)
    this.checkIsHomeLeftMenu()

    if (this.$refs.dockTrack) {
      this.stopDockMotion = initDockMotion(this.$refs.dockTrack)
    }

    document.addEventListener('keydown', this.onDocumentKeydown)
    this.walletSheetMedia = window.matchMedia('(max-width: 767px)')
    this.onWalletSheetMediaChange()
    if (this.walletSheetMedia.addEventListener) {
      this.walletSheetMedia.addEventListener('change', this.onWalletSheetMediaChange)
    } else {
      this.walletSheetMedia.addListener(this.onWalletSheetMediaChange)
    }
  },
  beforeDestroy() {
    if (this.stopDockMotion) this.stopDockMotion()

    document.removeEventListener('keydown', this.onDocumentKeydown)
    if (this.walletSheetMedia.removeEventListener) {
      this.walletSheetMedia.removeEventListener('change', this.onWalletSheetMediaChange)
    } else {
      this.walletSheetMedia.removeListener(this.onWalletSheetMediaChange)
    }
    this.syncWalletSheetModal(false)

    // Reset the body style when the component is destroyed
    this.toggleBodyStyle(false)
    document.body.classList.remove('mobile-menu-open')

    window.removeEventListener('scroll', this.handleScroll)
    this.enableScrolling()
  },
  methods: {
    openBlog() {
      window.open('https://www.katsuwin.ai/blog/', '_blank')
    },
    isMobile() {
      return detectMob()
    },
    openWheel() {
      this.toggleNav = false
      this.$store.commit('SET_WHEEL_OPEN', true)
    },
    openUserInfo(event) {
      event.stopPropagation()
      this.showUserInfo = !this.showUserInfo
    },
    dispatchV4Events() {
      let foundLottery = false
      let foundNews = false
      this.events.forEach(event => {
        if (event.type === 'signup_v4') {
          this.$store.commit('ADD_V4_ID', event.id)
        }
        if (event.type === 'daily_lottery_v2' && event.is_show === 1) {
          foundLottery = true
          this.$store.commit('ADD_LOTTERY_ID', event.id)
        }
        if (event.type === 'manual_first_deposit_v2') {
          foundNews = true
          this.$store.commit('ADD_NEWS_ID', event.id)
        }
        if (event.type === 'daily_attend') {
          this.isOpenDailyAttend = true
        }
      })
      if (!foundLottery) {
        this.$store.commit('ADD_LOTTERY_ID', null)
      }
      if (!foundNews) {
        this.$store.commit('ADD_NEWS_ID', null)
      }
    },
    closeWalletSheet() {
      this.showUserInfo = false
    },
    onWalletSheetMediaChange() {
      this.isWalletSheetViewport = this.walletSheetMedia.matches
      // 跨過斷點時收起，避免下拉選單與底部面板狀態混用
      this.showUserInfo = false
    },
    // 底部面板開啟時只保留面板可操作（其餘 inert），關閉時還原並把焦點交回錢包按鈕
    syncWalletSheetModal(open) {
      const sheet = this.$refs.walletSheet
      document.body.classList.toggle('wallet-sheet-open', open)
      if (open && sheet) {
        this.walletSheetInert = new Map()
        let node = sheet
        while (node.parentElement && node !== document.body) {
          for (const sibling of node.parentElement.children) {
            if (sibling === node || sibling.classList.contains('wallet-sheet-backdrop') || /^(SCRIPT|STYLE|LINK)$/.test(sibling.tagName)) continue
            this.walletSheetInert.set(sibling, sibling.inert)
            sibling.inert = true
          }
          node = node.parentElement
        }
        this.$nextTick(() => {
          const selected = this.$refs.walletList && this.$refs.walletList.querySelector('[aria-checked="true"]')
          ;(selected || sheet).focus({ preventScroll: true })
        })
      } else if (this.walletSheetInert) {
        this.walletSheetInert.forEach((value, element) => { element.inert = value })
        this.walletSheetInert = null
        this.walletSheetDrag = null
        this.walletSheetDragY = 0
        if (this.$refs.walletTrigger) this.$refs.walletTrigger.focus({ preventScroll: true })
      }
    },
    onWalletSheetHeadClose() {
      if (performance.now() >= this.suppressWalletSheetClickUntil) this.closeWalletSheet()
    },
    onWalletSheetKeydown(event) {
      if (!this.isWalletSheetOpen) return
      const item = event.target.closest('[role="radio"]')
      if (item && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault()
        item.click()
        return
      }
      if (item && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
        event.preventDefault()
        const items = [...this.$refs.walletList.children]
        const index = items.indexOf(item)
        const next = event.key === 'Home' ? 0
          : event.key === 'End' ? items.length - 1
            : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length
        items[next].focus()
        return
      }
      if (event.key === 'Tab') {
        const controls = [...this.$refs.walletSheet.querySelectorAll('button:not(:disabled), [tabindex="0"]')].filter(el => el.getClientRects().length)
        const first = controls[0]
        const last = controls[controls.length - 1]
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault()
          last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault()
          first.focus()
        }
      }
    },
    onWalletSheetDragStart(event) {
      if (event.button !== 0) return
      this.walletSheetDrag = { id: event.pointerId, y: event.clientY }
    },
    onWalletSheetDragMove(event) {
      if (!this.walletSheetDrag || this.walletSheetDrag.id !== event.pointerId) return
      const delta = Math.max(0, event.clientY - this.walletSheetDrag.y)
      // 移動超過 8px 才當作拖曳，避免吃掉關閉鈕的點擊
      if (delta > 8) {
        event.currentTarget.setPointerCapture(event.pointerId)
        this.walletSheetDragY = delta
      }
    },
    // 往下拖超過 72px 關閉面板
    onWalletSheetDragEnd(event) {
      if (!this.walletSheetDrag || this.walletSheetDrag.id !== event.pointerId) return
      const delta = this.walletSheetDragY
      this.walletSheetDrag = null
      this.walletSheetDragY = 0
      if (delta > 8) this.suppressWalletSheetClickUntil = performance.now() + 300
      if (delta > 72 && event.type !== 'pointercancel') this.closeWalletSheet()
    },
    onDocumentKeydown(event) {
      if (event.key !== 'Escape') return
      if (this.isWalletSheetOpen) {
        event.preventDefault()
        this.closeWalletSheet()
      } else if (this.toggleNav && this.isMobile()) {
        this.toggleNav = false
      }
    },
    onDockKeydown(event) {
      if ((event.key === 'Enter' || event.key === ' ') && event.target.tagName === 'LI') {
        event.preventDefault()
        event.target.click()
      }
    },
    toggleBodyStyle(open) {
      if (open) {
        document.body.style.overflow = 'hidden'
        document.body.style.overscrollBehavior = 'none'
      } else {
        document.body.style.overflow = ''
        document.body.style.overscrollBehavior = ''
      }
    },
    changeGameCategory(item) {
      if (!this.isHome) {
        // 從其他頁面進入：直接前往分類路徑，避免多一筆首頁紀錄
        this.$router.push(item && item.seo_path ? { name: 'home', params: { seo_path: item.seo_path }} : '/')
      }

      if (!this.game.isGameReady) return

      this.selectedTab = item
      this.setToggleNavAndCommit()
      const filterValue = item ? 'others' : 'favorites'
      this.$store.commit('SET_CURRENT_GAME_PROVIDER', null)
      if (item) {
        item.attribute = 'category'
      } else if (this.$route.params.seo_path) {
        // 我的最愛沒有分類路徑，離開目前的分類網址
        this.$router.push('/')
      }
      this.$store.commit('SET_CURRENT_GAME_CATEGORY', item)
      this.$store.commit('SET_ACTIVE_FILTER', filterValue)
    },
    setLanguage(key) {
      if (this.brandInfo.language.includes(key)) {
        this.selectedLanguage = key
      } else {
        this.selectedLanguage = this.brandInfo.language[0]
      }
    },
    changeLanguage(key) {
      this.setLanguage(key)
      this.setToggleNavAndCommit()
      var url = new URL(document.location.href)
      url.searchParams.set('language', key)
      document.location.href = url
    },
    checkDailyNotice() {
      if (!this.token) {
        this.showDailyNotice = false
        return
      }

      const currentTimeStamp = Math.floor(Date.now() / 1000)
      const storedTimeStamp = localStorage.getItem('lastDailyNoticeTimeStamp')

      if (!storedTimeStamp || isCrossDay(currentTimeStamp, parseInt(storedTimeStamp, 10))) {
        page({ type: 'index_pop_notice' }).then(response => {
          this.dailyNoticeData = response.data.data

          if (this.dailyNoticeData && this.dailyNoticeData.setting) {
            if (!this.dailyNoticeData.setting.allow_member_levels) {
              this.showDailyNotice = true
              localStorage.setItem('lastDailyNoticeTimeStamp', currentTimeStamp.toString())
              return
            }
            const allowedMemberLevels = this.dailyNoticeData.setting.allow_member_levels
              .split(',')
              .map(Number)
              .filter(n => n)

            if (allowedMemberLevels.includes(this.memberInfo.vl)) {
              this.showDailyNotice = true
              localStorage.setItem('lastDailyNoticeTimeStamp', currentTimeStamp.toString())
            }
          }
        }).catch(error => {
          console.error('Failed to fetch daily notice:', error)
          this.showDailyNotice = false
        })
      } else {
        this.showDailyNotice = false
      }
    },
    goHome() {
      if (!this.isHome) {
        this.$router.push('/')
        this.initCurrentGame()
      } else {
        if (this.isRegisterActive) {
          this.$store.commit('SET_REGISTER_OPEN', false)
        } else if (this.isLoginActive) {
          this.$store.commit('SET_LOGIN_OPEN', false)
        } else if (this.isForgetActive) {
          this.$store.commit('SET_FORGET_OPEN', false)
        } else if (Object.keys(this.$route.query).length > 0) {
          this.$router.replace({ query: {}})
          this.initCurrentGame()
        } else {
          this.initCurrentGame()
        }
      }

      this.setToggleNavAndCommit()
    },
    goSignup() {
      if (window.gtag) {
        window.gtag('event', 'register_click', {
          'event_category': 'button',
          'event_label': 'register_button',
          'value': 1
        })
      }

      this.setToggleNavAndCommit()
      this.$store.commit('SET_REGISTER_OPEN', true)
    },
    goLogin() {
      this.setToggleNavAndCommit()
      this.$store.commit('SET_LOGIN_OPEN', true)
    },
    logout() {
      this.setToggleNavAndCommit()
      localLogout()
    },
    checkIsHomeLeftMenu() {
      const personalMap = ['Personal', 'BetHistory', 'TransactionHistory', 'Bank', 'Message', 'Bonus', 'BankCard', 'UserBonus', 'UserCommission', 'News']
      let isPersonalMenu = false
      if (personalMap.indexOf(this.$route.name) >= 0) isPersonalMenu = true

      const otherMap = ['Coupon', 'AllBonus', 'VipIndex', 'eventList']
      let isOtherMenu = false
      if (otherMap.indexOf(this.$route.name) >= 0) isOtherMenu = true

      const bottomMap = ['UserFriend', 'EditName', 'KYC', 'Support', 'Verify']
      let isBottomMenu = false
      if (bottomMap.indexOf(this.$route.name) >= 0) isBottomMenu = true

      if (this.isHome) {
        return
      }

      this.$nextTick(() => {
        setTimeout(() => {
          if (isPersonalMenu) {
            this.toogleMyAccout = true
            this.scrollToNavElement('navPersonal')
          } else if (isOtherMenu) {
            this.scrollToNavElement('navOther')
          } else {
            const navMenu = this.$refs.navMenu
            let top = 0
            if (isBottomMenu) {
              top = navMenu.scrollHeight
            }

            navMenu.scrollTo({
              top: top,
              left: 0,
              behavior: 'smooth'
            })
          }
        }, 300)
      })
    },
    setActiveTab(tab) {
      this.activeTab = tab
      this.$store.commit('SET_CURRENT_GAME_CATEGORY', null)
    },
    isActiveTab(tab) {
      return this.activeTab === tab
    },
    // 底部 dock 依目前路由選取（首頁不選取任何項目）
    getRouteTab() {
      if (this.path.startsWith('/event')) return 'offers'
      if (this.path.startsWith('/deposit')) return 'deposit'
      if (this.path.startsWith('/user')) return 'my'
      if (this.path.startsWith('/points')) return 'shop'
      if (this.path === '/page/support') return 'support'
      return ''
    },
    // 搜尋元件掛在首頁，其他頁面先回首頁再開啟
    openMobileSearch() {
      const open = () => {
        this.$store.commit('SET_SHOW_PROVIDER', false)
        this.$store.commit('SET_SHOW_RESEARCH', true)
      }
      if (this.isHome) {
        open()
      } else {
        // 首頁 initIndex 會關閉搜尋，等首頁渲染完再開啟
        this.$router.push('/').then(() => this.$nextTick(open)).catch(() => {})
      }
    },
    setToggleNavAndCommit() {
      this.toggleNav = false
      this.showUserInfo = false
      this.$store.commit('SET_REGISTER_OPEN', false)
      this.$store.commit('SET_LOGIN_OPEN', false)
      this.$store.commit('SET_FORGET_OPEN', false)
    },
    // 手機 dock 客服：同側選單客服快捷列；沒有快捷列時同 chatbox，兩者皆無則不動作
    goDockSupport() {
      if (this.quickBars.length) this.goQuickBar(this.quickBars[0])
      else if (this.banner) this.goHelpCenter()
    },
    goQuickBar(item) {
      const isGuest = !this.token
      const dateFormatter = this.$options.filters.dateFormat
      handleHelpCenterLink(isGuest, item, this.user, this.memberInfo, this.user.vipInfo, dateFormatter)
    },
    goHelpCenter() {
      const isGuest = !this.token
      const user = this.user
      const userInfo = this.memberInfo
      const vipInfo = this.user.vipInfo
      const dateFormatter = this.$options.filters.dateFormat

      handleHelpCenterLink(isGuest, this.banner, user, userInfo, vipInfo, dateFormatter)
    },
    goProxy() {
      // 導向各站台自己的代理後台（VUE_APP_AGENT_URL）並帶上 token；沒有代理後台的站台（留空）點擊不做任何動作
      const url = getAffiliateUrl()
      if (!url) return
      window.open(url, '_blank')
    },
    handleLinkClick(path) {
      if (!this.token) {
        this.toggleNav = false
        this.$store.commit('SET_LOGIN_OPEN', true)
      } else {
        this.$router.push(path)
      }
    },
    jumptoShop() {
      this.$router.push('/points/all-bonus')
    },
    handleScroll() {
      const scrollTop = document.documentElement.scrollTop
      if (scrollTop > this.lastScrollTop) {
        // Scrolling down
        this.showNav = false
      } else {
        // Scrolling up
        this.showNav = true
      }
      this.lastScrollTop = scrollTop <= 0 ? 0 : scrollTop
    },
    scrollToNavElement(refName) {
      this.$nextTick(() => {
        const navMenu = this.$refs.navMenu

        const navElement = this.$refs[refName]
        let position = 100
        if (this.token) {
          position = 250
        }

        if (navMenu && navElement) {
          const navElementPosition = navElement.offsetTop - position
          navMenu.scrollTo({ top: navElementPosition, behavior: 'smooth' })
        }
      })
    },
    disableScrolling() {
      document.body.style.overflow = 'hidden'
    },
    enableScrolling() {
      document.body.style.overflow = ''
    },
    getDailyItem(item) {
      window.open(item.link, item.open_type)
      this.$store.commit('SET_DAILY_ITEM', item)
    },
    initCurrentGame() {
      this.selectedTab = null
      if (!localStorage.getItem('selectedWallet')) {
        this.selectedWalletId = null
      }

      this.$store.commit('SET_ACTIVE_FILTER', 'others')
      this.$store.commit('SET_CURRENT_GAME_PROVIDER', null)
      this.$store.commit('SET_CURRENT_GAME_CATEGORY', null)

      if (this.$route.params.seo_path) {
        this.$router.replace('/')
      }

      const navMenu = this.$refs.navMenu
      navMenu.scrollTo({
        top: 0,
        left: 0,
        behavior: 'smooth'
      })
    },
    init() {
    // 初始化
      this.$store.commit('SET_FORGET_OPEN', this.user.isValidateFail)
      this.$store.commit('SET_REGISTER_OPEN', false)
      this.$store.commit('SET_LOGIN_OPEN', false)
      this.$store.commit('SET_CHECKOUT_COUNTER_OPEN', false)
      this.$store.commit('SET_CHECKOUT_COUNTER_BCPAY_OPEN', false)
      this.$store.commit('SET_RESET_PASSWORD_OPEN', false)
      this.$store.commit('SET_VAILTADA_FAIL', false)
      this.$store.commit('SET_SHOW_PROVIDER', false)
      this.$store.commit('SET_SHOW_RESEARCH', false)
      this.$store.commit('SET_CURRENT_GAME_PROVIDER', null)
      this.$store.commit('SET_ACTIVE_FILTER', 'others')
      this.$store.commit('SET_FROM_MENU', false)
      if (localStorage.getItem('fromLandingPage')) {
        localStorage.removeItem('fromLandingPage')
      } else {
        this.$store.commit('SET_CURRENT_GAME_CATEGORY', null)
      }
    },
    selectWallet(wallet) {
      this.selectedWalletId = {
        wallet_type: wallet.wallet_type,
        currency: wallet.currency
      }
      this.showUserInfo = false

      // Save selected wallet to localStorage for persistence
      localStorage.setItem('selectedWallet', JSON.stringify(this.selectedWalletId))
      this.$store.commit('SET_SELECTED_WALLET', wallet)

      // Force immediate re-render of wallet images
      this.$nextTick(() => {
        // This empty nextTick ensures Vue completes the DOM update cycle
      })
    },
    isWalletSelected(wallet) {
      if (!this.selectedWalletId) return false
      return this.selectedWalletId.wallet_type === wallet.wallet_type &&
             this.selectedWalletId.currency === wallet.currency
    },
    initSelectedWallet() {
      console.log('initSelectedWallet called', {
        walletInfo: this.walletInfo,
        filteredWalletInfo: this.filteredWalletInfo,
        filteredLength: this.filteredWalletInfo?.length || 0,
        currentSelectedWalletId: this.selectedWalletId
      })
      // Try to restore previously selected wallet from localStorage
      const storedWallet = localStorage.getItem('selectedWallet')
      if (storedWallet) {
        try {
          const parsedWallet = JSON.parse(storedWallet)
          // Check if the stored wallet still exists in the current wallet list
          const foundWallet = this.filteredWalletInfo.find(w =>
            w.wallet_type === parsedWallet.wallet_type &&
            w.currency === parsedWallet.currency
          )
          if (foundWallet) {
            this.selectedWalletId = {
              wallet_type: foundWallet.wallet_type,
              currency: foundWallet.currency
            }

            this.$store.commit('SET_SELECTED_WALLET', foundWallet)
            return
          }
        } catch (error) {
          console.error('Error parsing stored wallet:', error)
        }
      }

      // Fallback to default wallet selection
      const defaultMain = this.filteredWalletInfo.find(w => w.wallet_type === 'main' && w.currency === this.defaultWalletCurrency)
      if (defaultMain) {
        this.selectedWalletId = {
          wallet_type: defaultMain.wallet_type,
          currency: defaultMain.currency
        }
        this.$store.commit('SET_SELECTED_WALLET', defaultMain)
      } else if (this.filteredWalletInfo.length > 0) {
        const first = this.filteredWalletInfo[0]
        this.selectedWalletId = {
          wallet_type: first.wallet_type,
          currency: first.currency
        }
        this.$store.commit('SET_SELECTED_WALLET', first)
      }
    },
    getWalletBalance(wallet) {
      if (!wallet) return '0'

      const balance = parseFloat(wallet.balance) || 0

      if (wallet.currency === 'JPY') {
        // JPY: 無條件捨去到兩位小數
        const truncated = Math.floor(balance * 100) / 100
        const isInteger = truncated % 1 === 0
        return isInteger
          ? truncated.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
          : truncated.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      } else {
        // 其他幣別：顯示到小數點以下第六位,無條件捨去後面位數
        const truncated = Math.floor(balance * 1e6) / 1e6
        return truncated.toFixed(6)
      }
    },
    getWalletImagePath,
    isUserDataComplete() {
      return !isPersonalDataComplete(this.memberInfo)
    },
    goToDeposit() {
      this.showUserInfo = false
      // 會員層級入金停用時不進入存款頁
      if (!this.$store.getters.canMemberDeposit) return

      if (this.isUserDataComplete() && !this.isTheme('theme_purple')) {
        const self = this
        showNotice(this.$t('Bao.deposit_notes'), function() {
          self.$router.push('/deposit')
        }, true, false, '')
        return
      }

      if (this.$route.path !== '/deposit') {
        this.$router.push('/deposit')
      }
    },
    goToWithdrawal() {
      this.showUserInfo = false

      if (this.$route.path !== '/withdrawal') {
        this.$router.push('/withdrawal')
      }
    }
  }
}
</script>

